import React, { useState, useEffect } from 'react';
import { apiLogin, deleteSession, getSessions, getToken, saveSessionConfig, updateSession } from '../storage';
import { getApiBase } from '../apiBase';
import { toast } from '../ui.jsx';

const API_BASE = getApiBase();

// ── SessionPicker ─────────────────────────────────────────────────
const CARRY_OPTIONS = [
  { k: 'classes', icon: 'class', label: 'Classes', desc: 'Class list & sections' },
  { k: 'teachers', icon: 'person_book', label: 'Teachers', desc: 'Staff directory' },
  { k: 'tt', icon: 'calendar_today', label: 'Timetable', desc: 'Period schedule' },
  { k: 'slots', icon: 'schedule', label: 'Time Slots', desc: 'Period timings' },
  { k: 'feeStruct', icon: 'payments', label: 'Fee Structure', desc: 'Monthly fee & charges' },
  { k: 'settings', icon: 'settings', label: 'School Settings', desc: 'School info & config' },
  { k: 'events', icon: 'event', label: 'Calendar Events', desc: 'Holidays & events' },
];

function SessionPicker({ sessions: initSessions, authSession, onPick, onCreate, onSignOut, onRefresh }) {
  const [sessions, setSessions] = useState(initSessions);
  const [sessionStats, setSessionStats] = useState({});
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newYear, setNewYear] = useState('');
  const [nameErr, setNameErr] = useState('');
  const [mounted, setMounted] = useState(false);
  const [hovIdx, setHovIdx] = useState(null);
  const [carry, setCarry] = useState({ classes: false, teachers: false, tt: false, slots: false, feeStruct: false, settings: false, events: false });
  // edit state
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editYear, setEditYear] = useState('');
  // delete confirm state
  const [delId, setDelId] = useState(null);
  const [delConfirm, setDelConfirm] = useState('');
  const [delErr, setDelErr] = useState('');
  useEffect(() => { const t = setTimeout(() => setMounted(true), 60); return () => clearTimeout(t); }, []);

  // Sync when parent updates sessions list
  useEffect(() => { setSessions(initSessions); }, [initSessions]);

  // Load lightweight stats per session
  useEffect(() => {
    const token = getToken();
    initSessions.forEach(s => {
      const sid = s.sid || s.id;
      Promise.all([
        fetch(`${API_BASE}/sessions/${sid}/students`, { headers: { Authorization: 'Bearer ' + token } }).then(r => r.json()).catch(() => []),
        fetch(`${API_BASE}/sessions/${sid}/payments`, { headers: { Authorization: 'Bearer ' + token } }).then(r => r.json()).catch(() => []),
      ]).then(([students, pays]) => {
        setSessionStats(prev => ({
          ...prev,
          [sid]: {
            stuCount: Array.isArray(students) ? students.length : 0,
            col: Array.isArray(pays) ? pays.reduce((sum, p) => sum + (p.amt || 0), 0) : 0,
          }
        }));
      });
    });
  }, [initSessions]);

  const hasPrev = sessions.length > 0;
  const allSelected = CARRY_OPTIONS.every(o => carry[o.k]);
  const toggleCarry = k => setCarry(c => ({ ...c, [k]: !c[k] }));
  const toggleAll = () => {
    const val = !allSelected;
    setCarry(Object.fromEntries(CARRY_OPTIONS.map(o => [o.k, val])));
  };

  const resetCreate = () => {
    setCreating(false); setNameErr(''); setNewName(''); setNewYear('');
    setCarry({ classes: false, teachers: false, tt: false, slots: false, feeStruct: false, settings: false, events: false });
  };

  const doCreate = () => {
    if (!newName.trim()) { setNameErr('Session name required'); return; }
    if (!newYear.trim()) { setNameErr('Academic year required'); return; }
    onCreate(newName.trim(), newYear.trim(), carry);
  };

  const startEdit = (s, e) => { e.stopPropagation(); setEditId(s.sid || s.id); setEditName(s.name); setEditYear(s.year); };
  const cancelEdit = () => { setEditId(null); setEditName(''); setEditYear(''); };
  const doEdit = async (e) => {
    e.stopPropagation();
    if (!editName.trim() || !editYear.trim()) return;
    try {
      await updateSession(editId, editName.trim(), editYear.trim());
      // Also update the session config so settings.year reflects the new year
      await saveSessionConfig(editId, { settings: { year: editYear.trim(), reportAcademicYear: editYear.trim() } });
      if (onRefresh) onRefresh();
    } catch (err) { toast('Update failed: ' + err.message, 'err'); }
    cancelEdit();
  };

  const startDelete = (s, e) => { e.stopPropagation(); setDelId(s.sid || s.id); setDelConfirm(''); setDelErr(''); };
  const cancelDelete = () => { setDelId(null); setDelConfirm(''); setDelErr(''); };
  const doDelete = async () => {
    if (!delConfirm.trim()) { setDelErr('Enter admin password'); return; }
    try {
      await apiLogin(authSession?.user?.username, delConfirm);
    } catch (e) {
      setDelErr('Incorrect password');
      return;
    }
    try {
      await deleteSession(delId);
      if (onRefresh) onRefresh();
    } catch (err) { toast('Delete failed: ' + err.message, 'err'); }
    cancelDelete();
  };

  const anim = (delay = 0) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'none' : 'translateY(20px)',
    transition: `opacity 550ms ${delay}ms cubic-bezier(0.4,0,0.2,1), transform 550ms ${delay}ms cubic-bezier(0.4,0,0.2,1)`,
  });

  return (
    <div className="sp-root" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', background: 'linear-gradient(145deg,#001530 0%,#002045 50%,#0a3060 100%)', position: 'relative', overflow: 'hidden', fontFamily: 'Inter,sans-serif' }}>
      <style>{`
        @keyframes sp-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}}
        @keyframes sp-pulse{0%,100%{opacity:0.4}50%{opacity:0.9}}
        @keyframes sp-slidein{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
        @keyframes sp-spin{to{transform:rotate(360deg)}}
        .sp-sess{transition:all 200ms!important}
        .sp-sess:hover .sp-actions{opacity:1!important}
        .sp-sess:hover{border-color:#60a5fa!important;background:rgba(255,255,255,0.08)!important;transform:translateY(-3px)!important;box-shadow:0 12px 32px rgba(0,0,0,0.3)!important}
        .sp-create:hover{border-color:#60a5fa!important;color:#60a5fa!important;background:rgba(96,165,250,0.08)!important}
        .sp-input:focus{border-color:#60a5fa!important;box-shadow:0 0 0 3px rgba(96,165,250,0.15)!important;background:rgba(255,255,255,0.08)!important}
        .sp-input{transition:border-color 200ms,box-shadow 200ms,background 200ms}
        .sp-icon-btn{background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.12);border-radius:8px;width:30px;height:30px;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all 150ms;color:rgba(255,255,255,0.5)}
        .sp-icon-btn:hover{background:rgba(255,255,255,0.14)!important;color:#fff!important}
        .sp-icon-btn.del:hover{background:rgba(239,68,68,0.2)!important;border-color:rgba(239,68,68,0.4)!important;color:#fca5a5!important}
        .sp-icon-btn.edit:hover{background:rgba(96,165,250,0.15)!important;border-color:rgba(96,165,250,0.4)!important;color:#60a5fa!important}
        .sp-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.7);backdrop-filter:blur(4px);z-index:100;display:flex;align-items:center;justify-content:center;padding:16px}
        @keyframes sp-modal{from{opacity:0;transform:scale(0.92)}to{opacity:1;transform:none}}
        @media (max-width: 768px){
          .sp-root{padding:14px 10px!important}
          .sp-wrap{max-width:100%!important}
          .sp-head h2{font-size:30px!important}
          .sp-grid-2{grid-template-columns:1fr!important}
          .sp-actions{opacity:1!important}
          .sp-cta-row{flex-direction:column!important}
        }
      `}</style>

      {/* Background grid */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)', backgroundSize: '52px 52px', pointerEvents: 'none' }} />
      {/* Orbs */}
      {[{ w: 400, h: 400, top: '-120px', right: '-100px', dur: '20s', delay: '0s' }, { w: 250, h: 250, bottom: '-80px', left: '-60px', dur: '16s', delay: '4s' }, { w: 120, h: 120, top: '40%', left: '10%', dur: '12s', delay: '2s' }].map((o, i) => (
        <div key={i} style={{ position: 'absolute', width: o.w, height: o.h, top: o.top, right: o.right, bottom: o.bottom, left: o.left, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', filter: 'blur(1px)', animation: `sp-float ${o.dur} ${o.delay} ease-in-out infinite`, pointerEvents: 'none' }} />
      ))}

      <div className="sp-wrap" style={{ width: '100%', maxWidth: 540, position: 'relative', zIndex: 1 }}>

        {/* Header */}
        <div className="sp-head" style={{ ...anim(0), textAlign: 'center', marginBottom: 40 }}>
          {/* School name badge — no logo box */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '10px 22px', borderRadius: 50, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(12px)', marginBottom: 28, boxShadow: '0 4px 24px rgba(0,0,0,0.25)' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#60a5fa' }}>school</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>LORD KRISHNA PUBLIC SCHOOL</span>
          </div>

          {/* Big greeting */}
          <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.38)', letterSpacing: '0.06em', marginBottom: 10 }}>
            Welcome back, <span style={{ color: '#60a5fa', fontWeight: 800 }}>{authSession?.user?.name || 'Admin'}</span>
          </div>
          <h2 style={{ fontSize: 38, fontWeight: 900, color: '#fff', fontFamily: 'Manrope,sans-serif', letterSpacing: '-0.03em', margin: '0 0 10px', lineHeight: 1.05 }}>
            Select Academic<br />
            <span style={{ background: 'linear-gradient(90deg,#60a5fa,#a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Session</span>
          </h2>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.38)', margin: 0 }}>Choose a session to continue or start a new one</p>
          {/* Sign out */}
          <button onClick={onSignOut}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 18, padding: '7px 18px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 150ms', backdropFilter: 'blur(8px)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.12)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.35)'; e.currentTarget.style.color = '#fca5a5'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}>
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>logout</span>
            Sign Out
          </button>
        </div>

        {/* Session cards */}
        <div style={{ ...anim(80), display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
          {sessions.map((s, i) => {
            const key = s.sid || s.id;
            const stats = sessionStats[key] || {};
            const stuCount = stats.stuCount || 0;
            const col = stats.col || 0;
            const isLatest = i === sessions.length - 1;
            const isHov = hovIdx === i;
            const isEditing = editId === key;
            return (
              <div key={key} style={{ position: 'relative' }}>
                {isEditing ? (
                  /* ── Inline edit form ── */
                  <div style={{ padding: '16px 18px', borderRadius: 16, border: '1.5px solid rgba(96,165,250,0.5)', background: 'rgba(96,165,250,0.08)', backdropFilter: 'blur(8px)', animation: 'sp-slidein 200ms ease' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Edit Session</div>
                    <div className="sp-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                      {[{ label: 'Session Name', val: editName, set: setEditName }, { label: 'Academic Year', val: editYear, set: setEditYear }].map(f => (
                        <div key={f.label}>
                          <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 5 }}>{f.label}</div>
                          <input value={f.val} onChange={e => f.set(e.target.value)} className="sp-input"
                            style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', fontSize: 12, fontWeight: 600, color: '#fff', outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={cancelEdit} style={{ flex: 1, padding: '9px', borderRadius: 9, border: '1.5px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.55)', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>Cancel</button>
                      <button onClick={doEdit} style={{ flex: 2, padding: '9px', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#1960a3,#60a5fa)', color: '#fff', fontWeight: 800, fontSize: 11, cursor: 'pointer' }}>Save Changes</button>
                    </div>
                  </div>
                ) : (
                  /* ── Normal card ── */
                  <div className="sp-sess" role="button" tabIndex={0}
                    onClick={() => onPick(s.sid || s.id)}
                    onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onPick(s.sid || s.id)}
                    onMouseEnter={() => setHovIdx(i)} onMouseLeave={() => setHovIdx(null)}
                    style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 18px', borderRadius: 16, border: `1.5px solid ${isLatest ? 'rgba(96,165,250,0.5)' : 'rgba(255,255,255,0.1)'}`, background: isLatest ? 'rgba(96,165,250,0.1)' : 'rgba(255,255,255,0.05)', cursor: 'pointer', textAlign: 'left', backdropFilter: 'blur(8px)', boxShadow: isLatest ? '0 4px 20px rgba(0,0,0,0.2)' : 'none', width: '100%', boxSizing: 'border-box' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: isLatest ? 'linear-gradient(135deg,#1960a3,#60a5fa)' : 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: isLatest ? '0 4px 12px rgba(25,96,163,0.4)' : 'none' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#fff' }}>school</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                        <span style={{ fontSize: 14, fontWeight: 800, color: '#fff', fontFamily: 'Manrope,sans-serif' }}>{s.name}</span>
                        {isLatest && <span style={{ fontSize: 9, fontWeight: 700, color: '#60a5fa', background: 'rgba(96,165,250,0.15)', padding: '2px 9px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.07em', border: '1px solid rgba(96,165,250,0.3)' }}>Latest</span>}
                      </div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', display: 'flex', gap: 10 }}>
                        <span>{stuCount} student{stuCount !== 1 ? 's' : ''}</span>
                        <span>·</span>
                        <span>₹{col.toLocaleString('en-IN')} collected</span>
                      </div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.28)', marginTop: 2 }}>Created {new Date(s.created).toLocaleDateString('en-IN')}</div>
                    </div>
                    {/* Action buttons — visible on hover */}
                    <div className="sp-actions" style={{ display: 'flex', gap: 6, opacity: 0, transition: 'opacity 150ms', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                      <button className="sp-icon-btn edit" title="Edit" onClick={e => startEdit(s, e)}>
                        <span className="material-symbols-outlined" style={{ fontSize: 15 }}>edit</span>
                      </button>
                      {sessions.length > 1 && (
                        <button className="sp-icon-btn del" title="Delete" onClick={e => startDelete(s, e)}>
                          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>delete</span>
                        </button>
                      )}
                    </div>
                    <span className="material-symbols-outlined" style={{ fontSize: 18, color: isLatest ? '#60a5fa' : 'rgba(255,255,255,0.35)', flexShrink: 0, transition: 'transform 200ms', transform: isHov ? 'translateX(4px)' : 'translateX(0)', marginLeft: 4 }}>arrow_forward</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Delete confirmation modal */}
        {delId && (
          <div className="sp-overlay" onClick={cancelDelete}>
            <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 380, borderRadius: 18, border: '1.5px solid rgba(239,68,68,0.4)', background: 'linear-gradient(145deg,#1a0a0a,#2a0f0f)', padding: '28px 26px', animation: 'sp-modal 220ms ease', boxShadow: '0 24px 60px rgba(0,0,0,0.6)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(239,68,68,0.2)', border: '1.5px solid rgba(239,68,68,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 22, color: '#f87171' }}>delete_forever</span>
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', fontFamily: 'Manrope,sans-serif' }}>Delete Session</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>"{sessions.find(s => (s.sid || s.id) === delId)?.name}"</div>
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, marginBottom: 18 }}>
                This will permanently delete the session and <span style={{ color: '#fca5a5', fontWeight: 700 }}>all its data</span> including students, payments, attendance and exams. This cannot be undone.
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 7 }}>Enter admin password to confirm</div>
                <input value={delConfirm} onChange={e => { setDelConfirm(e.target.value); setDelErr(''); }}
                  type="password" placeholder="Admin password"
                  className="sp-input"
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${delErr ? 'rgba(239,68,68,0.6)' : 'rgba(255,255,255,0.15)'}`, background: 'rgba(255,255,255,0.06)', fontSize: 14, fontWeight: 700, color: '#fff', outline: 'none', boxSizing: 'border-box', letterSpacing: '0.05em' }} />
                {delErr && <div style={{ fontSize: 11, color: '#fca5a5', marginTop: 6 }}>{delErr}</div>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={cancelDelete} style={{ flex: 1, padding: '11px', borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.6)', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                <button onClick={doDelete} style={{ flex: 1, padding: '11px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#b91c1c,#ef4444)', color: '#fff', fontWeight: 800, fontSize: 12, cursor: 'pointer', boxShadow: '0 4px 16px rgba(185,28,28,0.4)' }}>Delete Session</button>
              </div>
            </div>
          </div>
        )}

        {/* Create new */}
        <div style={anim(160)}>
          {!creating ? (
            <button className="sp-create"
              onClick={() => setCreating(true)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '15px', borderRadius: 14, border: '1.5px dashed rgba(255,255,255,0.2)', background: 'transparent', color: 'rgba(255,255,255,0.5)', fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all 180ms', boxSizing: 'border-box' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add_circle</span>
              Create New Session
            </button>
          ) : (
            <div style={{ borderRadius: 16, border: '1.5px solid rgba(96,165,250,0.4)', background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)', animation: 'sp-slidein 300ms ease', overflow: 'hidden' }}>
              {/* Form header */}
              <div style={{ padding: '18px 22px 0', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 16, marginBottom: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', fontFamily: 'Manrope,sans-serif', marginBottom: 4 }}>New Academic Session</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)' }}>Configure what to carry over from the previous session</div>
              </div>

              <div style={{ padding: '18px 22px' }}>
                {nameErr && <div style={{ fontSize: 11, color: '#fca5a5', background: 'rgba(239,68,68,0.15)', padding: '9px 12px', borderRadius: 9, marginBottom: 14, border: '1px solid rgba(239,68,68,0.3)' }}>{nameErr}</div>}

                {/* Name + Year inputs */}
                <div className="sp-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                  {[{ label: 'Session Name', val: newName, set: v => { setNewName(v); setNameErr(''); }, ph: 'e.g. 2026-2027' }, { label: 'Academic Year', val: newYear, set: v => { setNewYear(v); setNameErr(''); }, ph: 'e.g. 2026-2027' }].map(f => (
                    <div key={f.label}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>{f.label}</div>
                      <input value={f.val} onChange={e => f.set(e.target.value)} placeholder={f.ph}
                        className="sp-input"
                        style={{ width: '100%', padding: '10px 13px', borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', fontSize: 13, fontWeight: 600, color: '#fff', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  ))}
                </div>

                {/* Carry-over section — only if there are previous sessions */}
                {hasPrev && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Carry Over from Previous Session</div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>Select what to import into the new session</div>
                      </div>
                      <button onClick={toggleAll}
                        style={{ fontSize: 10, fontWeight: 700, color: allSelected ? '#fbbf24' : '#60a5fa', background: allSelected ? 'rgba(251,191,36,0.1)' : 'rgba(96,165,250,0.1)', border: `1px solid ${allSelected ? 'rgba(251,191,36,0.3)' : 'rgba(96,165,250,0.3)'}`, borderRadius: 20, padding: '4px 12px', cursor: 'pointer', transition: 'all 150ms', whiteSpace: 'nowrap' }}>
                        {allSelected ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>

                    <div className="sp-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {CARRY_OPTIONS.map(opt => {
                        const on = carry[opt.k];
                        return (
                          <button key={opt.k} onClick={() => toggleCarry(opt.k)}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 11, border: `1.5px solid ${on ? 'rgba(96,165,250,0.6)' : 'rgba(255,255,255,0.1)'}`, background: on ? 'rgba(96,165,250,0.12)' : 'rgba(255,255,255,0.04)', cursor: 'pointer', textAlign: 'left', transition: 'all 150ms' }}>
                            <div style={{ width: 30, height: 30, borderRadius: 8, background: on ? 'linear-gradient(135deg,#1960a3,#60a5fa)' : 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 150ms' }}>
                              <span className="material-symbols-outlined" style={{ fontSize: 15, color: on ? '#fff' : 'rgba(255,255,255,0.4)' }}>{opt.icon}</span>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: on ? '#fff' : 'rgba(255,255,255,0.6)', lineHeight: 1.2 }}>{opt.label}</div>
                              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.desc}</div>
                            </div>
                            <div style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${on ? '#60a5fa' : 'rgba(255,255,255,0.2)'}`, background: on ? '#60a5fa' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 150ms' }}>
                              {on && <span className="material-symbols-outlined" style={{ fontSize: 11, color: '#fff', fontVariationSettings: "'FILL' 1" }}>check</span>}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Always-fresh note */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '9px 12px', borderRadius: 9, background: 'rgba(251,191,36,0.07)', border: '1px solid rgba(251,191,36,0.2)' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 14, color: '#fbbf24', flexShrink: 0 }}>info</span>
                      <span style={{ fontSize: 10, color: 'rgba(251,191,36,0.8)', lineHeight: 1.4 }}>Always fresh: Students, attendance, payments, exams &amp; marks are never carried over</span>
                    </div>
                  </div>
                )}


                <div className="sp-cta-row" style={{ display: 'flex', gap: 8 }}>
                  <button onClick={resetCreate}
                    style={{ flex: 1, padding: '11px', borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.6)', fontWeight: 700, fontSize: 12, cursor: 'pointer', transition: 'all 150ms' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                    Cancel
                  </button>
                  <button onClick={doCreate}
                    style={{ flex: 2, padding: '11px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#1960a3,#60a5fa)', color: '#fff', fontWeight: 800, fontSize: 12, cursor: 'pointer', boxShadow: '0 4px 16px rgba(25,96,163,0.4)', transition: 'all 150ms' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(25,96,163,0.5)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(25,96,163,0.4)'; }}>
                    Create &amp; Enter Session
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}


export default SessionPicker;
